import { useCallback, useContext, useEffect, useMemo } from 'react';
import {
  AudioTags,
  IAudioManagerReturnedObject,
  IEventHandlerOptions,
  tTaskAudioManagerItems
} from '../models';
import useAudioPreferences from './useAudioPreferences';
import AudioContext from '../AudioContext';

interface IReturnedObject {
  playById: IAudioManagerReturnedObject['playById'];
}

export default function useTaskAudioPlayer(
  items: tTaskAudioManagerItems | null,
  ignorePreferences = false
): IReturnedObject {
  // No page-visibility listener here on purpose: it would repeat the same
  // manager-wide call once per mounted consumer. It lives in `useAudioManager`.
  const {
    addUnique,
    playById,
    unregisterUncached,
    addTagToMute,
    removeTagFromMute,
    getById
  } = useContext(AudioContext);

  const cleanedAudioItems = useMemo(
    () =>
      items?.map(({ autoplay, ...item }) => {
        return item;
      }),
    [items]
  );

  const handlePlay = useCallback(
    (options: IEventHandlerOptions, onPlay?: (options: IEventHandlerOptions) => void) => {
      onPlay && onPlay(options);
    },
    []
  );

  /**
   * Turning the music on is idempotent: every mounted consumer registers the same
   * app-wide items and runs this on mount, so N consumers (WordNodes: one per
   * NodePoint) meant N `playById` calls for one track. Only 'played' is skipped —
   * 'paused' (deliberate) and 'visible-paused' (backgrounded) must still start.
   */
  const handleBGMusicToggle = useCallback(
    (turnOn: boolean) => {
      items?.forEach((item) => {
        if (!item.tags?.includes(AudioTags.MusicPlayback)) return;

        const player = getById(item.id)?.player;

        if (!turnOn) {
          player?.stop();

          return;
        }

        if (player?.status !== 'played') playById(item.id);
      });
    },
    [items, playById, getById]
  );

  useAudioPreferences(
    addTagToMute,
    removeTagFromMute,
    ignorePreferences,
    handleBGMusicToggle
  );

  useEffect(() => {
    cleanedAudioItems?.forEach((item) => {
      const { onPlay } = item;

      addUnique({
        ...item,
        onPlay: (options) => {
          handlePlay(options, onPlay);
        }
      });
    });

    handleBGMusicToggle(true);

    return () => {
      // unregister and delete from collection tracks registered by this hook only
      cleanedAudioItems?.forEach((item) => {
        unregisterUncached(item.id);
      });
    };
  }, [addUnique, handlePlay, cleanedAudioItems, unregisterUncached, handleBGMusicToggle]);

  return { playById };
}
